import { h } from '@omni-torn/ui';
import { ESTIMATE_METHOD_WORDS, estimateTrustWord, spyWhose, type EstimateFacts, type EstimateMethod, type PlayerEstimateAnswer } from '@omni-torn/shared';
import { ago, box, lineChart, num, short, table } from '../ui';

/**
 * Omni-Torn's estimate of a player as the website draws it — one copy for Spies & estimates (pages/spies.ts) and the
 * player page (pages/lookup.ts): the tiles (best estimate, how far to trust it, the newest spy) and the history chart.
 * Data: GET /api/estimates/player/:id (the player database's estimates only — never fights or who sent what).
 */

export const methodWord = (m: string | null): string => (m ? (ESTIMATE_METHOD_WORDS[m as EstimateMethod] ?? m) : '—');
const STATUS_WORD: Record<string, string> = { Okay: 'Okay', Hospital: 'In hospital', Jail: 'In jail', Traveling: 'Traveling', Abroad: 'Abroad', Federal: 'In federal jail', Fallen: 'Fallen' };
export const statusText = (p: Pick<EstimateFacts, 'status' | 'statusUntil'>): string => (p.status ? `${STATUS_WORD[p.status] ?? p.status}${p.statusUntil && p.statusUntil > Date.now() ? ` (until ${new Date(p.statusUntil).toISOString().slice(11, 16)} TCT)` : ''}` : '—');
export const lastAction = (ms: number | null): string => (ms ? ago(ms < 1e12 ? ms * 1000 : ms) : '—');
export const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export const ESTIMATE_NOTE = 'The estimate weighs each way of working it out by how far it can be trusted and how old it is: a recent spy beats everything, then fights, then pooled fair fights, then rank.';

/** Best estimate (with how it was worked out), trust (with its age) and, when there is one, the newest spy. */
export function estimateTiles(a: PlayerEstimateAnswer): HTMLElement {
  const p = a.player;
  const age = p?.totalAt ? Date.now() - p.totalAt : 0;
  return h(
    'div',
    { class: 'tiles', 'data-testid': 'spy-estimate' },
    h('div', { class: 'tile tile-total' }, h('div', { class: 'tile-label' }, 'Best estimate'), h('div', { class: 'tile-value', title: num(p?.total) }, short(p?.total)), h('div', { class: 'muted' }, p?.total ? `from ${methodWord(p.totalSource)}` : 'none yet')),
    h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, 'Trust'), h('div', { class: 'tile-value' }, p?.total ? estimateTrustWord(p.totalSource, age) : '—'), h('div', { class: 'muted' }, p?.totalAt ? `${ago(p.totalAt)}` : '')),
    a.spies[0] ? h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, 'Newest spy'), h('div', { class: 'tile-value', title: num(a.spies[0].total) }, short(a.spies[0].total)), h('div', { class: 'muted' }, `${ago(a.spies[0].at)} · ${spyWhose(a.spies[0])}`)) : null,
  );
}

/** The estimate over time: a chart and the day-by-day table (newest first). */
export function estimateHistoryBox(a: PlayerEstimateAnswer, opts: { id?: string } = {}): HTMLElement {
  return box(
    'Estimate history',
    [
      lineChart(a.history.map((x) => ({ at: x.at, v: x.total })), { label: 'Estimated total' }),
      table([...a.history].reverse(), [{ label: 'Day', get: (x) => day(x.at), sort: (x) => x.at }, { label: 'Estimate', get: (x) => short(x.total), sort: (x) => x.total, cls: 'r' }, { label: 'From', get: (x) => methodWord(x.method) }], { empty: 'No estimates yet.', initial: 0 }),
    ],
    opts.id ? { id: opts.id } : {},
  );
}
